import { Component, HostListener, forwardRef, Input, Output, EventEmitter, ChangeDetectionStrategy, OnInit, ChangeDetectorRef } from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { ListItem, IDropdownAutoCompleteSettings } from "./multiselect.model";
import { ListFilterPipe } from "./list-filter.pipe";
import * as _ from 'lodash';
import { HttpClient } from '@angular/common/http';

export const DROPDOWN_CONTROL_VALUE_ACCESSOR: any = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => MultiSelectAutoCompleteComponent),
  multi: true
};
const noop = () => { };

@Component({
  selector: 'multi-select-auto-complete',
  templateUrl: './multi-select-auto-complete.component.html',
  styleUrls: ['./multi-select.component.scss'],
  providers: [DROPDOWN_CONTROL_VALUE_ACCESSOR],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class MultiSelectAutoCompleteComponent implements OnInit {
  public _settings: IDropdownAutoCompleteSettings;
  public _data: Array<ListItem> = [];
  public selectedItems: Array<ListItem> = [];
  public isDropdownOpen = true;
  _placeholder = "Select";
  private _sourceDataFields: Array<String> = []; // store source data fields names
  private hasMore: boolean = false;
  loading: boolean = false;
  filter: ListItem = { id: '', text: '' };
  defaultSettings: IDropdownAutoCompleteSettings = {
    singleSelection: false,
    idField: "id",
    textField: "text",
    disabledField: "isDisabled",
    enableCheckAll: true,
    selectAllText: "Select All",
    unSelectAllText: "UnSelect All",
    allowSearchFilter: false,
    limitSelection: -1,
    clearSearchFilter: true,
    maxHeight: 197,
    searchPlaceholderText: "Search",
    noDataAvailablePlaceholderText: "No data available",
    closeDropDownOnSelection: false,
    showSelectedItemsAtTop: false,
    defaultOpen: false,
    allowRemoteDataSearch: false,
    showChipSelection: true,
    alwaysOpen: false,
    endPoint: '',
    params: {
      limit: 20,
      offset: 0,
      searchString: ''
    }
  };

  @Input()
  public set placeholder(value: string) {
    if (value) {
      this._placeholder = value;
    } else {
      this._placeholder = "Select";
    }
  }

  @Input()
  disabled = false;

  @Input()
  public set settings(value: IDropdownAutoCompleteSettings) {
    if (value) {
      this._settings = Object.assign(this.defaultSettings, value);
    } else {
      this._settings = Object.assign(this.defaultSettings);
    }
  }

  get selectedData() {
    return this.selectedItems;
  }

  @Input()
  set selectedData(values) {
    this.selectedItems = values;
  }

  @Output("selectedDataChange") selectedDataChange = new EventEmitter<any>();

  @Output("onFilterChange")
  onFilterChange: EventEmitter<ListItem> = new EventEmitter<any>();

  @Output("onDropDownClose")
  onDropDownClose: EventEmitter<ListItem> = new EventEmitter<any>();

  @Output("onSelect")
  onSelect: EventEmitter<ListItem> = new EventEmitter<any>();

  @Output("onDeSelect")
  onDeSelect: EventEmitter<ListItem> = new EventEmitter<any>();

  @Output("onSelectAll")
  onSelectAll: EventEmitter<Array<ListItem>> = new EventEmitter<Array<any>>();

  @Output("onDeSelectAll")
  onDeSelectAll: EventEmitter<Array<ListItem>> = new EventEmitter<Array<any>>();

  private onTouchedCallback: () => void = noop;
  private onChangeCallback: (_: any) => void = noop;

  onFilterTextChange($event) {
    this.fetchList();
    this.onFilterChange.emit($event);
  }

  constructor(
    private listFilterPipe: ListFilterPipe,
    private http: HttpClient,
    private cdRef: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.fetchList();
  }

  fetchList() {
    this._settings.params.searchString = this.filter.text || '';

    let promise: Promise<any>;
    if (this._settings.endPoint) {
      this.loading = true;
      promise = this.http.get(this._settings.endPoint, { params: this._settings.params }).toPromise();
    }

    promise.then((response: any) => {
      let mappedResponse = this.defaultResponseMapper(response, this._settings.params.offset, this._settings.params.limit);

      if (this._settings.params.offset == 0) {
        this._data = mappedResponse.results;
      } else {
        this._data = [...this._data, ...mappedResponse.results];
      }
      this.hasMore = mappedResponse.hasMore;
      this.loading = false;
      this.cdRef.detectChanges();
    }).catch((err) => {
      if (this._settings.params.offset == 0) {
        this._data = [];
        this.loading = false;
      }
      this.hasMore = false;
      this.cdRef.detectChanges();
    });
  }

  defaultResponseMapper(response: any, offset: number, limit: number) {
    // if responseMapper is not provided, will use this default one
    let items: ListItem[] = [];
    if (response.results) {
      items = response.results.map((item) => {
        return {
          id: item.id,
          text: item.name
        }
      });
    }
    let hasMore = (response.totalNum && response.totalNum > offset + limit) || response.hasMore;
    return {
      results: items,
      hasMore: !!hasMore
    }
  }

  handleOnScroll() {
    if (this.hasMore) {
      this._settings.params.offset += this._settings.params.limit;
      this.fetchList();
    }
  }

  onItemClick($event: any, item: ListItem) {
    if (this.disabled || item.isDisabled) {
      return false;
    }

    const found = this.isSelected(item);
    const allowAdd = this._settings.limitSelection === -1 || (this._settings.limitSelection > 0 && this.selectedItems.length < this._settings.limitSelection);
    if (!found) {
      if (allowAdd) {
        this.addSelected(item);
      }
    } else {
      this.removeSelected(item);
    }
    if (this._settings.singleSelection && this._settings.closeDropDownOnSelection) {
      this.closeDropdown();
    }
  }

  resetSelectedList() {
    let emitValue = this.emittedValue(this.selectedItems);
    this.selectedItems.length = 0;
    this.onChangeCallback(emitValue);
    this.selectedDataChange.emit(this.selectedItems);
    this.onDeSelectAll.emit(emitValue);
  }

  // From ControlValueAccessor interface
  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  // From ControlValueAccessor interface
  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  // Set touched on blur
  @HostListener("blur")
  public onTouched() {
    this.closeDropdown();
    this.onTouchedCallback();
  }

  trackByFn(index, item) {
    return item.id;
  }

  isSelected(clickedItem: ListItem) {
    return _.some(this.selectedItems, { 'id': clickedItem.id })
  }

  isLimitSelectionReached(): boolean {
    return this._settings.limitSelection === this.selectedItems.length;
  }

  isAllItemsSelected(): boolean {
    // get disabld item count
    let filteredItems = this.listFilterPipe.transform(this._data, this.filter, this._settings.dataLimit);
    const itemDisabledCount = filteredItems.filter(item => item.isDisabled).length;
    // take disabled items into consideration when checking
    if ((!this._data || this._data.length === 0) && this._settings.allowRemoteDataSearch) {
      return false;
    }
    return filteredItems.length === _.intersection(_.map(this.selectedItems, 'id'), _.map(filteredItems, 'id')).length + itemDisabledCount;
  }

  showButton(): boolean {
    if (!this._settings.singleSelection) {
      if (this._settings.limitSelection > 0) {
        return false;
      }
      return true;
    } else {
      // should be disabled in single selection mode
      return false;
    }
  }

  addSelected(item: ListItem) {
    if (this._settings.singleSelection) {
      this.selectedItems.length = 0;
      this.selectedItems.push(item);
    } else {
      this.selectedItems.push(item);
    }
    this.onChangeCallback(this.emittedValue(this.selectedItems));
    this.selectedDataChange.emit(this.selectedItems);
    this.onSelect.emit(this.emittedValue(item));
  }

  removeSelected(itemSel: ListItem) {
    this.selectedItems.forEach((item, i) => {
      if (itemSel.id === item.id) {
        this.selectedItems.splice(i, 1);
      }
    });
    this.onChangeCallback(this.emittedValue(this.selectedItems));
    this.selectedDataChange.emit(this.selectedItems);
    this.onDeSelect.emit(this.emittedValue(itemSel));
  }

  emittedValue(val: any): any {
    const selected = [];
    if (Array.isArray(val)) {
      val.map(item => {
        selected.push(this.objectify(item));
      });
    } else {
      if (val) {
        return this.objectify(val);
      }
    }
    return selected;
  }

  objectify(val: ListItem) {
    const obj = {};
    obj[this._settings.idField] = val.id;
    obj[this._settings.textField] = val.text;
    if (this._sourceDataFields.includes(this._settings.disabledField)) {
      obj[this._settings.disabledField] = val.isDisabled;
    }
    return obj;
  }

  toggleDropdown(evt) {
    evt.preventDefault();
    if (this.disabled && this._settings.singleSelection) {
      return;
    }
    this._settings.defaultOpen = !this._settings.defaultOpen;
    if (!this._settings.defaultOpen) {
      this.onDropDownClose.emit();
    }
  }

  closeDropdown() {
    if (this._settings) {
      this._settings.defaultOpen = false;
      // clear search text
      if (this._settings.clearSearchFilter) {
        this.filter.text = "";
      }
    }
    this.onDropDownClose.emit();
  }

  toggleSelectAll() {
    if (this.disabled) {
      return false;
    }
    let filteredItem = this.listFilterPipe.transform(this._data, this.filter, this._settings.dataLimit).filter(item => !item.isDisabled).slice();
    if (!this.isAllItemsSelected()) {
      this.selectedItems = _.uniqBy([...this.selectedItems, ...filteredItem], "id");
      this.onChangeCallback(this.emittedValue(this.selectedItems));
      this.selectedDataChange.emit(this.selectedItems);
      this.onSelectAll.emit(this.emittedValue(this.selectedItems));
    } else {
      this.selectedItems = _.pullAllBy(this.selectedItems, filteredItem, "id");
      this.onChangeCallback(this.emittedValue(this.selectedItems));
      this.selectedDataChange.emit(this.selectedItems);
      this.onDeSelectAll.emit(this.emittedValue(this.selectedItems));
    }
  }

  getFields(inputData) {
    const fields = [];
    if (typeof inputData !== "object") {
      return fields;
    }
    // tslint:disable-next-line:forin
    for (const prop in inputData) {
      fields.push(prop);
    }
    return fields;
  }

}
